import type { Editor } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { BlockContextItem, HandlebarsBlockType } from '../../types';

/**
 * Computes the block nesting context at the current cursor position.
 * Walks up the node tree from cursor, collecting ancestor handlebarsBlock nodes.
 * O(depth) instead of O(document_size).
 *
 * @param editor - The Tiptap editor instance
 * @returns Array of enclosing blocks (outermost first, innermost last)
 */
export function computeBlockContext(editor: Editor): BlockContextItem[] {
  const { state } = editor;
  const { selection } = state;
  return computeBlockContextAt(editor, selection.from);
}

/**
 * Computes the block nesting context at a given document position.
 * Walks up the node tree, collecting ancestor handlebarsBlock nodes.
 *
 * @param editor - The Tiptap editor instance
 * @param pos - Document position to resolve context from
 * @returns Array of enclosing blocks (outermost first, innermost last)
 */
export function computeBlockContextAt(editor: Editor, pos: number): BlockContextItem[] {
  const $pos = editor.state.doc.resolve(pos);
  const context: BlockContextItem[] = [];

  for (let depth = $pos.depth; depth > 0; depth--) {
    const node = $pos.node(depth);
    if (node.type.name === 'handlebarsBlock') {
      context.unshift({
        type: node.attrs.blockType as HandlebarsBlockType,
        variable: node.attrs.variable,
        blockId: node.attrs.blockId,
      });
    }
  }

  return context;
}

/**
 * Finds the matching end node for a given block's blockId.
 *
 * @param editor - The Tiptap editor instance
 * @param blockId - The block ID to search for
 * @returns Position and node info, or null if not found
 */
export function findMatchingBlockEnd(
  editor: Editor,
  blockId: string
): { pos: number; node: ProseMirrorNode } | null {
  const { doc } = editor.state;
  let result: { pos: number; node: ProseMirrorNode } | null = null;

  doc.descendants((node, pos) => {
    if (
      node.type.name === 'handlebarsBlockEnd' &&
      node.attrs.blockId === blockId
    ) {
      result = { pos, node };
      return false; // Stop searching
    }
  });

  return result;
}

/**
 * Finds the matching start node for a given block's blockId.
 *
 * @param editor - The Tiptap editor instance
 * @param blockId - The block ID to search for
 * @returns Position and node info, or null if not found
 */
export function findMatchingBlockStart(
  editor: Editor,
  blockId: string
): { pos: number; node: ProseMirrorNode } | null {
  const { doc } = editor.state;
  let result: { pos: number; node: ProseMirrorNode } | null = null;

  doc.descendants((node, pos) => {
    if (
      node.type.name === 'handlebarsBlockStart' &&
      node.attrs.blockId === blockId
    ) {
      result = { pos, node };
      return false; // Stop searching
    }
  });

  return result;
}

/**
 * Validates all block pairs in the document.
 *
 * @param editor - The Tiptap editor instance
 * @returns List of orphaned or invalid nodes
 */
export interface BlockValidationResult {
  type: 'orphaned-start' | 'orphaned-end' | 'type-mismatch';
  pos: number;
  blockId: string;
  blockType: HandlebarsBlockType;
}

export function validateBlockPairs(editor: Editor): BlockValidationResult[] {
  const { doc } = editor.state;
  const results: BlockValidationResult[] = [];

  const startNodes: Map<string, { pos: number; blockType: HandlebarsBlockType }> = new Map();
  const endNodes: Map<string, { pos: number; blockType: HandlebarsBlockType }> = new Map();

  // Collect all start and end nodes
  doc.descendants((node, pos) => {
    if (node.type.name === 'handlebarsBlockStart') {
      startNodes.set(node.attrs.blockId, {
        pos,
        blockType: node.attrs.blockType,
      });
    } else if (node.type.name === 'handlebarsBlockEnd') {
      endNodes.set(node.attrs.blockId, {
        pos,
        blockType: node.attrs.blockType,
      });
    }
  });

  // Find orphaned starts (no matching end)
  for (const [blockId, { pos, blockType }] of startNodes) {
    const matchingEnd = endNodes.get(blockId);
    if (!matchingEnd) {
      results.push({
        type: 'orphaned-start',
        pos,
        blockId,
        blockType,
      });
    } else if (matchingEnd.blockType !== blockType) {
      results.push({
        type: 'type-mismatch',
        pos,
        blockId,
        blockType,
      });
    }
  }

  // Find orphaned ends (no matching start)
  for (const [blockId, { pos, blockType }] of endNodes) {
    if (!startNodes.has(blockId)) {
      results.push({
        type: 'orphaned-end',
        pos,
        blockId,
        blockType,
      });
    }
  }

  return results;
}

/**
 * Generates a unique block ID.
 */
export function generateBlockId(): string {
  return `block-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

